import React, { useState} from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import DownloadAppButton from '../components/DownloadAppButton';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { 
  WrenchScrewdriverIcon, 
  HomeIcon, 
  ComputerDesktopIcon,
  TruckIcon,
  SparklesIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

// Imports des images du carrousel
import slide1Img from '../assets/images/carousel/slide1.jpg';
import slide2Img from '../assets/images/carousel/slide1.jpg';
import slide3Img from '../assets/images/carousel/slide1.jpg';

const categories = [
  {
    id: 'artisanat',
    name: 'Artisanat & Réparations',
    icon: WrenchScrewdriverIcon,
    description: 'Plomberie, électricité, menuiserie, maçonnerie — trouvez l\'artisan qualifié pour chaque besoin.',
    subServices: ['Plomberie', 'Électricité', 'Menuiserie', 'Maçonnerie', 'Peinture', 'Serrurerie'],
  },
  {
    id: 'domicile',
    name: 'Services à domicile',
    icon: HomeIcon,
    description: 'Ménage, jardinage, garde d\'enfants, cuisine — des prestataires vérifiés pour votre confort quotidien.',
    subServices: ['Ménage', 'Jardinage', 'Garde d\'enfants', 'Cuisine', 'Repassage', 'Couture'],
  },
  {
    id: 'numerique',
    name: 'Services numériques',
    icon: ComputerDesktopIcon,
    description: 'Design, développement web, marketing digital, community management — des experts à votre service.',
    subServices: ['Design graphique', 'Développement web', 'Marketing digital', 'Community management', 'SEO', 'Montage vidéo'],
  },
  {
    id: 'transport',
    name: 'Livraison & Transport',
    icon: TruckIcon,
    description: 'Coursiers, déménageurs, chauffeurs — des solutions logistiques rapides et fiables.',
    subServices: ['Livraison express', 'Déménagement', 'Chauffeur privé', 'Transport de marchandises', 'Courses'],
  },
  {
    id: 'beaute',
    name: 'Beauté & Bien-être',
    icon: SparklesIcon,
    description: 'Coiffure, maquillage, soins — des professionnels certifiés qui se déplacent à domicile.',
    subServices: ['Coiffure', 'Maquillage', 'Manucure', 'Massage', 'Soins du visage', 'Tatouage'],
  },
  {
    id: 'formation',
    name: 'Formation & Coaching',
    icon: AcademicCapIcon,
    description: 'Cours particuliers, coaching professionnel, formations certifiantes dispensées par des experts.',
    subServices: ['Cours de langues', 'Coaching professionnel', 'Formation informatique', 'Soutien scolaire', 'Musique'],
  },
];

const ServicesPage = () => {
  const [activeCategory, setActiveCategory] = useState('artisanat');
  const currentCategory = categories.find(c => c.id === activeCategory);

  return (
    <div className="bg-[#0a0a0a] min-h-screen py-12">
      <div className="container-custom">
        {/* Titre de la page */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl lg:text-5xl font-display font-bold mb-4">
            <span className="text-white">Nos </span>
            <span className="text-[#F97316]">Services</span>
          </h1>
          <p className="text-[#AAAAAA] text-lg max-w-2xl mx-auto">
            Explorez les services disponibles sur VADOM — de l'artisanat à la prestation professionnelle,
            tout est à portée d'un clic.
          </p>
        </motion.div>

        {/* ========== CARROUSEL STYLÉ ========== */}
<motion.div
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.2, duration: 0.6 }}
  className="mb-16 w-full"
>
  <Swiper
    modules={[Autoplay, Pagination, Navigation]}
    spaceBetween={0}
    slidesPerView={1}
    loop={true}
    autoplay={{
      delay: 2000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    }}
    pagination={{ clickable: true }}
    navigation={true}
    className="w-full rounded-2xl"
  >
    {/* Slide 1 */}
    <SwiperSlide>
      <div className="relative w-full aspect-[16/9] md:aspect-[21/9] lg:aspect-[24/9] overflow-hidden rounded-2xl">
        <img 
          src={slide1Img} 
          alt="VADOM Services"
          className="absolute inset-0 w-full h-full object-contain bg-[#0a0a0a]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent flex items-center">
          <div className="p-6 md:p-10 lg:p-12 max-w-lg">
            <h3 className="text-white text-xl md:text-2xl lg:text-3xl font-bold mb-2">Services à domicile</h3>
            <p className="text-white/80 text-sm md:text-base mb-4">Dépannage • Maintenance • Entretien</p>
            <a href="/book" className="inline-block bg-[#F97316] text-white px-5 md:px-6 py-2 rounded-full text-sm font-semibold hover:bg-[#e8630a] transition">
              Réserver →
            </a>
          </div>
        </div>
      </div>
    </SwiperSlide>

    {/* Slide 2 */}
    <SwiperSlide>
      <div className="relative w-full aspect-[16/9] md:aspect-[21/9] lg:aspect-[24/9] overflow-hidden rounded-2xl">
        <img 
          src={slide2Img} 
          alt="VADOM Services"
          className="absolute inset-0 w-full h-full object-contain bg-[#0a0a0a]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent flex items-center">
          <div className="p-6 md:p-10 lg:p-12 max-w-lg">
            <h3 className="text-white text-xl md:text-2xl lg:text-3xl font-bold mb-2">Artisans qualifiés</h3>
            <p className="text-white/80 text-sm md:text-base mb-4">Électricité • Plomberie • Menuiserie</p>
            <a href="/book" className="inline-block bg-[#F97316] text-white px-5 md:px-6 py-2 rounded-full text-sm font-semibold hover:bg-[#e8630a] transition">
              Réserver →
            </a>
          </div>
        </div>
      </div>
    </SwiperSlide>

    {/* Slide 3 */}
    <SwiperSlide>
      <div className="relative w-full aspect-[16/9] md:aspect-[21/9] lg:aspect-[24/9] overflow-hidden rounded-2xl">
        <img 
          src={slide3Img} 
          alt="Application VADOM"
          className="absolute inset-0 w-full h-full object-contain bg-[#0a0a0a]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent flex items-center">
          <div className="p-6 md:p-10 lg:p-12 max-w-lg">
            <h3 className="text-white text-xl md:text-2xl lg:text-3xl font-bold mb-2">Application mobile</h3>
            <p className="text-white/80 text-sm md:text-base mb-4">Disponible en téléchargement direct</p>
            <div className="flex flex-wrap gap-3">
              <DownloadAppButton className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 text-white text-sm inline-flex items-center gap-2 transition" />
            </div>
          </div>
        </div>
      </div>
    </SwiperSlide>
  </Swiper>
</motion.div>
        {/* Sélecteur de catégories */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
          {categories.map((cat) => (
            <motion.button
              key={cat.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveCategory(cat.id)}
              className={`p-4 rounded-2xl text-center transition-all ${
                activeCategory === cat.id
                  ? 'bg-[#F97316]/20 border-2 border-[#F97316]'
                  : 'bg-[#111111] border-2 border-[#252525] hover:border-[#F97316]/50'
              }`}
            >
              <cat.icon className={`h-8 w-8 mx-auto mb-2 ${
                activeCategory === cat.id ? 'text-[#F97316]' : 'text-[#AAAAAA]'
              }`} />
              <span className={`text-sm font-medium ${
                activeCategory === cat.id ? 'text-[#F97316]' : 'text-white'
              }`}>
                {cat.name.split(' & ')[0]}
              </span>
            </motion.button>
          ))}
        </div>

        {/* Contenu de la catégorie active */}
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-[#111111] rounded-2xl border border-[#252525] p-6 max-w-4xl mx-auto"
        >
          <div className="flex items-start space-x-4 mb-6">
            {currentCategory && (
              <>
                <currentCategory.icon className="h-12 w-12 text-[#F97316] flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-display font-bold text-white mb-2">
                    {currentCategory.name}
                  </h2>
                  <p className="text-[#AAAAAA]">
                    {currentCategory.description}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Sous-services */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {currentCategory?.subServices.map((service) => (
              <Link
                key={service}
                to={`/book?service=${service}&category=${currentCategory.id}`}
                className="p-4 bg-[#0a0a0a] rounded-xl border border-[#252525] 
                           hover:border-[#F97316]/50 hover:bg-[#1a1a1a] transition-all group"
              >
                <p className="font-medium text-white group-hover:text-[#F97316] transition-colors">
                  {service}
                </p>
                <p className="text-sm text-[#666666] mt-1">
                  {currentCategory.name}
                </p>
              </Link>
            ))}
          </div>
        </motion.div>

        {/* CTA App Mobile - Conservé mais simplifié */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center"
        >
          <div className="bg-[#111111] rounded-2xl border border-[#252525] p-8 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#F97316]/10 px-4 py-2 rounded-full mb-6">
              <span className="text-[#F97316] text-sm font-medium">Téléchargez l'application VADOM</span>
            </div>
            <p className="text-[#AAAAAA] mb-6">
              Pour une expérience optimale, utilisez notre application mobile. 
              Gratuite, en téléchargement direct sur Android.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              {/* Pas encore publiée sur les stores : téléchargement direct */}
              <DownloadAppButton
                variant="card"
                className="inline-flex items-center gap-3 bg-[#1a1a1a] hover:bg-[#252525] border border-[#333333] rounded-xl px-5 py-3 transition-all"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ServicesPage;